import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { blogPosts } from '../data/blogPosts';

const SITE_URL = 'https://www.pandhtherapy.co.uk';
const MAX_TITLE_LENGTH = 47; // " | PH Therapy" = 13 chars, target ~60 total

function metaTitle(title: string): string {
  const suffix = ' | PH Therapy';
  if (title.length + suffix.length <= 60) return title + suffix;
  return title.slice(0, MAX_TITLE_LENGTH).trim() + (title.length > MAX_TITLE_LENGTH ? '…' : '') + suffix;
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Post Not Found | PH Therapy</title>
          <meta name="description" content="The blog post you're looking for doesn't exist. Browse our other articles on exercise physiology and rehabilitation." />
        </Helmet>
        <Header />
        <main className="container" style={{ paddingTop: '6rem', paddingBottom: '4rem' }}>
          <h1>Post not found</h1>
          <Link to="/blog">Back to Blog</Link>
        </main>
        <Footer />
      </>
    );
  }

  const canonicalUrl = `${SITE_URL}/blog/${post.slug}`;
  const ogImage = post.image.startsWith('http') ? post.image : `${SITE_URL}/${post.image}`;
  const datePublished = (() => {
    try {
      const d = new Date(post.date);
      return isNaN(d.getTime()) ? undefined : d.toISOString().split('T')[0];
    } catch {
      return undefined;
    }
  })();

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    author: { '@type': 'Person', name: post.author.replace(/, Founder$/, '').trim() },
    ...(datePublished && { datePublished }),
    ...(ogImage && { image: ogImage }),
    url: canonicalUrl,
    publisher: { '@type': 'Organization', name: 'PH Therapy', url: SITE_URL },
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle(post.title)}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle(post.title)} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="PH Therapy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle(post.title)} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Header />
      <main className="container" style={{ paddingTop: '6rem', paddingBottom: '4rem' }}>
        <Link to="/blog" style={{ display: 'inline-block', marginBottom: '1rem' }}>
          ← Back to Blog
        </Link>
        <article>
          <h1>{post.title}</h1>
          <p className="date" style={{ marginBottom: '1rem' }}>
            {post.date} · {post.author}
          </p>
          <div
            className="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>
      </main>
      <Footer />
    </>
  );
};

export default BlogPostPage;
